<template>
  <div>
    <!-- Filtros -->
    <b-card no-body>
      <b-card-body>
        <b-row class="align-items-center">
          <b-col md="4">
            <v-select
              v-model="filters.companyIds"
              :options="companies"
              label="name"
              :reduce="(c) => c.id"
              placeholder="Empresas"
              multiple
              :clearable="true"
            />
          </b-col>

          <b-col md="4">
            <v-select
              v-model="filters.currency"
              :options="[
                { label: 'Soles', value: 'Soles' },
                { label: 'Dólares', value: 'Dólares' },
              ]"
              label="label"
              :reduce="(o) => o.value"
              placeholder="Moneda"
              :clearable="false"
            />
          </b-col>
          <b-col md="4" class="text-right">
            <b-button
              class="mr-2"
              variant="outline-secondary"
              @click="resetFilters"
              >Limpiar</b-button
            >
            <b-button variant="primary" @click="getChartData">Filtrar</b-button>
          </b-col>

          <b-col md="2" class="mt-2">
            <b-form-select
              v-model="filterType"
              :options="['Día', 'Mes', 'Año']"
            />
          </b-col>

          <b-col v-if="filterType === 'Día'" md="2" class="mt-2">
            <b-form-input
              v-model="filters.from"
              type="date"
              placeholder="Desde"
            />
          </b-col>
          <b-col v-if="filterType === 'Día'" md="2" class="mt-2">
            <b-form-input
              v-model="filters.to"
              type="date"
              placeholder="Hasta"
            />
          </b-col>

          <b-col v-if="filterType === 'Mes'" md="2" class="mt-2">
            <b-form-input
              v-model="filters.from"
              type="month"
              placeholder="Mes inicio"
            />
          </b-col>
          <b-col v-if="filterType === 'Mes'" md="2" class="mt-2">
            <b-form-input
              v-model="filters.to"
              type="month"
              placeholder="Mes fin"
            />
          </b-col>

          <b-col v-if="filterType === 'Año'" md="2" class="mt-2">
            <b-form-select
              v-model="filters.from"
              :options="yearOptions"
              placeholder="Año inicio"
            />
          </b-col>
          <b-col v-if="filterType === 'Año'" md="2" class="mt-2">
            <b-form-select
              v-model="filters.to"
              :options="yearOptions"
              placeholder="Año fin"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col md="12">
            <h3 class="mb-1">Balance actual por empresa</h3>
            <b-table
              :items="balancesEmpresa"
              :fields="[
                { key: 'company', label: 'Empresa' },
                {
                  key: 'balance',
                  label: 'Balance',
                  formatter: (val, key, item) =>
                    (item.currency === 'Dólares' ? '$' : 'S/.') +
                    ' ' +
                    this.balanceFormat(val),
                },
                { key: 'currency', label: 'Moneda' },
                {
                  key: 'date',
                  label: 'Fecha',
                  formatter: (val) => {
                    const d = new Date(val);
                    return (
                      d.getUTCDate().toString().padStart(2, '0') +
                      '/' +
                      (d.getUTCMonth() + 1).toString().padStart(2, '0') +
                      '/' +
                      d.getUTCFullYear()
                    );
                  },
                },
              ]"
              bordered
              small
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Gráficos -->
    <b-card class="mt-2">
      <b-row>
        <b-col md="6">
          <apexchart
            type="bar"
            height="300"
            :options="barOptions"
            :series="barSeries"
          />
        </b-col>
        <b-col md="6">
          <apexchart
            type="line"
            height="300"
            :options="lineOptions"
            :series="lineSeries"
          />
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col md="6">
          <apexchart
            type="donut"
            height="300"
            :options="donutOptions"
            :series="donutSeries"
          />
        </b-col>
        <b-col md="6">
          <apexchart
            type="pie"
            height="300"
            :options="categoryOptions"
            :series="categorySeries"
          />
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col md="12">
          <apexchart
            type="bar"
            height="350"
            :options="categoryStackedOptions"
            :series="categoryStackedSeries"
          />
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>
<script>
import Vue from "vue";
import VueApexCharts from "vue-apexcharts";
import vSelect from "vue-select";
import { BootstrapVue, BootstrapVueIcons, VBTooltip } from "bootstrap-vue";
import FinancialMovementService from "@/services/FinancialMovement";
import Ripple from "vue-ripple-directive";
import TransactionService from "@/services/TransactionService";
import moment from "moment";

Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);
Vue.component("apexchart", VueApexCharts);

function formatNumber(val) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
}

export default {
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  components: {
    apexchart: VueApexCharts,
    vSelect,
  },
  data() {
    return {
      filterType: "Mes",
      filters: {
        from: "",
        to: "",
        companyIds: [],
        currency: "Soles",
      },
      yearOptions: Array.from(
        { length: 20 },
        (_, i) => new Date().getFullYear() - i
      ),
      companies: [],
      barOptions: {},
      barSeries: [],
      categoryOptions: {},
      categorySeries: [],
      lineOptions: {},
      lineSeries: [],
      donutOptions: {},
      donutSeries: [],
      areaOptions: {},
      areaSeries: [],
      categoryStackedOptions: {},
      categoryStackedSeries: [],
      balancesEmpresa: [],
      accountDictionary: {
        "191-2266254-0-53": "GQR Soles",
        "191-2289861-1-17": "GQR Dólares",
        "191-6661536-0-40": "GQR Recaudadora Soles",
        "191-6661491-1-96": "GQR Recaudadora Dólares",
        "191-2545696-0-99": "KONIN Soles",
        "191-2548669-1-39": "KONIN Dólares",
        "191-2358595-0-90": "C&F Soles",
        "191-2443055-1-32": "C&F Dólares",
        "193-9827975-0-72": "NIKKEI Soles",
        "193-9845813-1-64": "NIKKEI Dólares",
      },
    };
  },
  mounted() {
    this.loadCompanies();
    this.getChartData();
  },
  methods: {
    balanceFormat(val) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val);
    },
    async loadCompanies() {
      const res = await FinancialMovementService.getUploads(this.$store);
      const seen = new Set();
      const validCompanies = res
        .map((u) => u.company)
        .filter((c) => c && !seen.has(c.id) && seen.add(c.id))
        .map((c) => ({
          ...c,
          name: this.accountDictionary[c.name] || c.name,
        }));

      this.companies = validCompanies;
    },

    async getChartData() {
      let from = this.filters.from;
      let to = this.filters.to;

      if (this.filterType === "Día") {
        if (from) {
          from = moment.utc(from, "YYYY-MM-DD").startOf("day").toISOString();
        }
        if (to) {
          to = moment.utc(to, "YYYY-MM-DD").endOf("day").toISOString();
        }
      }

      if (this.filterType === "Mes") {
        if (from) {
          from = moment(from, "YYYY-MM")
            .startOf("month")
            .format("YYYY-MM-DDTHH:mm:ss");
        }
        if (to) {
          to = moment(to, "YYYY-MM")
            .endOf("month")
            .format("YYYY-MM-DDTHH:mm:ss");
        }
      }

      if (this.filterType === "Año") {
        if (from) {
          from = moment.utc(String(from), "YYYY").startOf("year").toISOString();
        }
        if (to) {
          to = moment.utc(String(to), "YYYY").endOf("year").toISOString();
        }
      }

      const params = {
        filterType: this.filterType,
        from,
        to,
        companyIds: this.filters.companyIds,
        currency: this.filters.currency,
      };
      const ingresosPorCategoria = {};
      const egresosPorCategoria = {};
      const balancesPorEmpresa = {};
      const currencySymbol = this.filters.currency === "Dólares" ? "$" : "S/.";

      const res = await FinancialMovementService.getSummary(
        params,
        this.$store
      );

      const monthlyData = res?.monthlyData ?? [];
      const totals = res?.totals ?? { ingreso: 0, egreso: 0 };
      const timeSeries = res?.timeSeries ?? [];

      this.barOptions = {
        chart: { stacked: true },
        xaxis: { categories: monthlyData.map((x) => x.label) },
        legend: { position: "top" },
        yaxis: {
          labels: {
            formatter: (val) => formatNumber(val),
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${currencySymbol} ${formatNumber(val)}`,
          },
        },
        plotOptions: {
          bar: {
            columnWidth: "65%",
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => formatNumber(val),
          style: {
            fontSize: "12px",
            colors: ["#fff"],
          },
        },
      };
      this.barSeries = [
        { name: "Ingresos", data: monthlyData.map((x) => x.ingreso) },
        { name: "Egresos", data: monthlyData.map((x) => x.egreso) },
      ];

      this.lineOptions = {
        chart: { id: "line-chart" },
        xaxis: { categories: timeSeries.map((x) => x.label) },
        yaxis: {
          labels: {
            formatter: (val) => formatNumber(val),
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${currencySymbol} ${formatNumber(val)}`,
          },
        },
      };
      this.lineSeries = [
        { name: "Ingresos", data: timeSeries.map((x) => x.ingreso) },
        { name: "Egresos", data: timeSeries.map((x) => x.egreso) },
      ];

      this.donutOptions = {
        labels: ["Ingresos", "Egresos"],
        dataLabels: {
          enabled: true,
          formatter: (val) => `${formatNumber(val)}%`,
        },
        tooltip: {
          y: {
            formatter: (val) => `${currencySymbol} ${formatNumber(val)}`,
          },
        },
      };
      this.donutSeries = [totals.ingreso, totals.egreso];

      this.areaOptions = {
        chart: { type: "area" },
        xaxis: { categories: timeSeries.map((x) => x.label) },
      };
      this.areaSeries = [
        { name: "Ingresos acumulados", data: timeSeries.map((x) => x.ingreso) },
        { name: "Egresos acumulados", data: timeSeries.map((x) => x.egreso) },
      ];

      const categoryMap = {
        "COM.MANTENIM": "Costos de Mantenimiento",
        "COMIS.RECAUDACION": "Comisión de Recaudación",
        "IMPUESTO ITF": "Impuesto ITF",
        EFECTIVO: "Pago Efectivo",
      };

      const categoryTotals = {};

      const allTransactions = await TransactionService.getTransactions(
        this.$store
      );
      const transactions = allTransactions.filter(
        (tx) => tx.currency === this.filters.currency
      );
      transactions.forEach((tx) => {
        const companyId = tx.company?.id;
        const currency = tx.currency;
        const balance = tx.balance;
        const companyName =
          this.accountDictionary[tx.company?.name] || tx.company?.name;

        const dateUTC = tx.date;

        if (!balancesPorEmpresa[companyId]) {
          balancesPorEmpresa[companyId] = {
            date: dateUTC,
            balance,
            company: companyName,
            currency,
          };
        } else {
          const existingDate = new Date(balancesPorEmpresa[companyId].date);
          const txDate = new Date(dateUTC);
          if (txDate > existingDate) {
            balancesPorEmpresa[companyId] = {
              date: dateUTC,
              balance,
              company: companyName,
              currency,
            };
          }
        }

        const rawDesc = tx.description || "";
        const desc = rawDesc.toUpperCase().trim();

        let key = null;

        if (/^EFECTIVO0{3}\d+/.test(desc)) {
          key = "EFECTIVO";
        } else {
          key = Object.keys(categoryMap).find((k) => desc.includes(k));
        }

        const category = key ? categoryMap[key] : "Otros";

        const amount = Math.abs(tx.amount);

        if (!categoryTotals[category]) {
          categoryTotals[category] = 0;
        }
        categoryTotals[category] += 1;

        if (tx.amount >= 0) {
          ingresosPorCategoria[category] =
            (ingresosPorCategoria[category] || 0) + amount;
        } else {
          egresosPorCategoria[category] =
            (egresosPorCategoria[category] || 0) + amount;
        }
      });

      this.balancesEmpresa = Object.values(balancesPorEmpresa);

      const allCategories = Array.from(
        new Set([
          ...Object.keys(ingresosPorCategoria),
          ...Object.keys(egresosPorCategoria),
        ])
      );

      this.categoryStackedOptions = {
        chart: {
          stacked: true,
          toolbar: { show: true },
        },
        xaxis: {
          categories: allCategories,
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              return formatNumber(val);
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${currencySymbol} ${formatNumber(val)}`,
          },
        },
        legend: {
          position: "top",
        },
        plotOptions: {
          bar: {
            columnWidth: "40%",
            dataLabels: {
              position: "center",
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => formatNumber(val),
          style: {
            fontSize: "12px",
            colors: ["#fff"],
          },
        },
      };

      this.categoryStackedSeries = [
        {
          name: "Ingresos",
          data: allCategories.map((c) => ingresosPorCategoria[c] || 0),
        },
        {
          name: "Egresos",
          data: allCategories.map((c) => egresosPorCategoria[c] || 0),
        },
      ];

      this.categoryOptions = {
        labels: Object.keys(categoryTotals),
        dataLabels: {
          enabled: true,
          formatter: (val) => `${formatNumber(val)}%`,
        },
        tooltip: {
          custom: function ({ series, seriesIndex, w }) {
            const label = w.globals.labels[seriesIndex];
            const value = series[seriesIndex];
            return `<div style="padding:8px"><strong>${label}</strong>: ${value} transacciones</div>`;
          },
        },
      };

      this.categorySeries = Object.values(categoryTotals);
    },
    resetFilters() {
      this.filters = {
        from: "",
        to: "",
        companyIds: [],
        currency: "Soles",
      };
      this.getChartData();
    },
  },
};
</script>
